/**
 * -----------------------------------------------------------------------------
 * Account Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the account details.
 */

import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MaintenanceFlag } from "../../../api/types/Sysadmin";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDraggableToggleList,
  AdminFormFieldDropdown,
  AdminFormFieldDurationInput,
  AdminFormFieldInput,
  AdminFormFieldMultiCheckbox,
  TSortableToggleItem,
} from "../../../components/admin/formFields";
import TickCheckbox from "../../../components/tickCheckbox";
import {
  ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE,
  ACCOUNT_MAP_ICON_SIZE_OPTIONS,
  APP_CONFIG,
  INFOBOX_DISPLAY_OPTIONS,
  TIMEZONE_OPTIONS,
} from "../../../constants/constants";
import { useLoggedInUserData } from "../../../utils/user";
import {
  DEFAULT_OVERLAY_OPTIONS,
  DISTANCE_UNITS_OPTIONS,
  ECONOMY_UNITS_OPTIONS,
  ENABLE_MAP_CLUSTERING_OPTIONS,
  LATITUDE_LONGITUDE_FORMAT_OPTIONS,
  PRESSURE_UNITS_OPTIONS,
  ROUTE_LINE_THICKNESS_OPTIONS,
  SCORECARD_WEIGHT_FACTOR_OPTIONS,
  SPEED_UNITS_OPTIONS,
  TEMPRATURE_UNITS_OPTIONS,
  VOLUME_UNITS_OPTIONS,
} from "./constants";

interface AccountGeneralDetailFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => any;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  formikValidateForm?: () => Promise<any>;
  userCanEdit: boolean;
  loadingData: boolean;
  maintenanceFlag?: MaintenanceFlag;
  setModifiedCheckBoxData?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

const mirroredAssetToggleFields = [
  "deviceid",
  "description",
  "displayname",
  "drivername",
];

const syncMirroredAssetToggles = (
  sourceValues: string[],
  targetValues: string[],
  targetAllowedValues: string[]
) => {
  const sourceSet = new Set(sourceValues);
  const targetSet = new Set(
    targetValues.filter((value) => targetAllowedValues.includes(value))
  );

  mirroredAssetToggleFields.forEach((field) => {
    if (!targetAllowedValues.includes(field)) return;

    if (sourceSet.has(field)) {
      targetSet.add(field);
    } else {
      targetSet.delete(field);
    }
  });

  return Array.from(targetSet);
};

export const AccountGeneralDetailForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label={t("account_id")}
        type="text"
        id="account_id"
        name="account_id"
        value={values.account_id}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.account_id}
        touched={touched.account_id}
        disabled={!userCanEdit}
        detailsFormField={true}
        readOnly={true}
      />
      <AdminFormFieldInput
        label={t("account_description")}
        type="text"
        id="account_description"
        name="account_description"
        value={values.account_description}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.account_description}
        touched={touched.account_description}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <AdminFormFieldInput
        label={t("contact_name")}
        type="text"
        id="contact_name"
        name="contact_name"
        value={values.contact_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_name}
        touched={touched.contact_name}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldInput
        label={t("contact_phone_number")}
        type="text"
        id="contact_phone_number"
        name="contact_phone_number"
        value={values.contact_phone_number}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_phone_number}
        touched={touched.contact_phone_number}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldInput
        label={t("contact_email")}
        type="email"
        id="contact_email"
        name="contact_email"
        value={values.contact_email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.contact_email}
        touched={touched.contact_email}
        disabled={!userCanEdit}
        customWrapperClass="col-span-4"
      />
      <div className="col-span-12 grid grid-cols-12 gap-4">
        <AdminFormFieldCheckbox
          label={t("use_2fa")}
          id="use_2fa"
          type="checkbox"
          name="use_2fa"
          checked={values.use_2fa}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.use_two_factor}
          error={errors.use_two_factor}
          disabled={!userCanEdit}
          detailsFormField={true}
          customWrapperClass="col-span-6"
        />
        {/* <AdminFormFieldCheckbox
        label={t("toggleft")}
        id="toggleft"
        type="checkbox"
        name="toggleft"
        checked={values.toggleft}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.toggleft}
        error={errors.toggleft}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      /> */}
      </div>
      <AdminFormFieldInput
        label={t("private_cost")}
        type="text"
        id="private_cost"
        name="private_cost"
        value={values.private_cost}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.private_cost}
        touched={touched.private_cost}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      {/* <AdminFormFieldInput
        label={t("idle_gas_usage")}
        type="text"
        id="idle_gas_usage"
        name="idle_gas_usage"
        value={values.idle_gas_usage}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.idle_gas_usage}
        touched={touched.idle_gas_usage}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldInput
        label={t("distance_gas_usage")}
        type="text"
        id="distance_gas_usage"
        name="distance_gas_usage"
        value={values.distance_gas_usage}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.distance_gas_usage}
        touched={touched.distance_gas_usage}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      /> */}

      <div className="col-span-12 grid grid-cols-12">
        <AdminFormFieldInput
          label={t("auto_update_interval_for_maps")}
          type="text"
          id="auto_update_interval_for_maps"
          name="auto_update_interval_for_maps"
          value={values.auto_update_interval_for_maps}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.auto_update_interval_for_maps}
          touched={touched.auto_update_interval_for_maps}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          label={t("drivers_assigned_to_devices")}
          id="drivers_assigned_to_devices"
          type="checkbox"
          name="drivers_assigned_to_devices"
          checked={values.drivers_assigned_to_devices}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.drivers_assigned_to_devices}
          error={errors.drivers_assigned_to_devices}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12 grid grid-cols-12">
        <AdminFormFieldDropdown
          loadingData={loadingData}
          label={t("enable_map_clustering")}
          id="enable_map_clustering"
          name="enable_map_clustering"
          value={`${values.enable_map_clustering}`}
          options={ENABLE_MAP_CLUSTERING_OPTIONS}
          onChange={(e) => {
            formikSetValue("enable_map_clustering", e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched("enable_map_clustering", true);
            handleBlur(e);
          }}
          touched={touched.enable_map_clustering}
          error={errors.enable_map_clustering}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12 grid grid-cols-8 gap-4">
        <AdminFormFieldCheckbox
          label={t("open_reports_in_new_tab")}
          id="open_reports_in_new_tab"
          type="checkbox"
          name="open_reports_in_new_tab"
          checked={values.open_reports_in_new_tab}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.open_reports_in_new_tab}
          error={errors.open_reports_in_new_tab}
          disabled={!userCanEdit}
          detailsFormField={true}
        />

        <AdminFormFieldCheckbox
          label={t("sync_driverId_from_driver_admin")}
          id="sync_driverId_from_driver_admin"
          type="checkbox"
          name="sync_driverId_from_driver_admin"
          checked={values.sync_driverId_from_driver_admin}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.sync_driverId_from_driver_admin}
          error={errors.sync_driverId_from_driver_admin}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>
      <div className="col-span-12 grid grid-cols-8 gap-4">
        <AdminFormFieldCheckbox
          label={t("has_snowplows")}
          id="has_snowplows"
          type="checkbox"
          name="has_snowplows"
          checked={values.has_snowplows}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.has_snowplows}
          error={errors.has_snowplows}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
        <AdminFormFieldCheckbox
          label={t("hide_total_rows_in_csv")}
          id="hide_total_rows_in_csv"
          type="checkbox"
          name="hide_total_rows_in_csv"
          checked={values.hide_total_rows_in_csv}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.hide_total_rows_in_csv}
          error={errors.hide_total_rows_in_csv}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("timezone")}
        id="timezone"
        name="timezone"
        value={values.timezone}
        options={TIMEZONE_OPTIONS}
        onChange={(e) => {
          formikSetValue("timezone", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("timezone", true);
          handleBlur(e);
        }}
        touched={touched.timezone}
        error={errors.timezone}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("speed_units")}
        id="speed_units"
        name="speed_units"
        value={values.speed_units}
        options={SPEED_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("speed_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("speed_units", true);
          handleBlur(e);
        }}
        touched={touched.speed_units}
        error={errors.speed_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("distance_units")}
        id="distance_units"
        name="distance_units"
        value={values.distance_units}
        options={DISTANCE_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("distance_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("distance_units", true);
          handleBlur(e);
        }}
        touched={touched.distance_units}
        error={errors.distance_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("volume_units")}
        id="volume_units"
        name="volume_units"
        value={values.volume_units}
        options={VOLUME_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("volume_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("volume_units", true);
          handleBlur(e);
        }}
        touched={touched.volume_units}
        error={errors.volume_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("economy_units")}
        id="economy_units"
        name="economy_units"
        value={values.economy_units}
        options={ECONOMY_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("economy_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("economy_units", true);
          handleBlur(e);
        }}
        touched={touched.economy_units}
        error={errors.economy_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("pressure_units")}
        id="pressure_units"
        name="pressure_units"
        value={values.pressure_units}
        options={PRESSURE_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("pressure_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("pressure_units", true);
          handleBlur(e);
        }}
        touched={touched.pressure_units}
        error={errors.pressure_units}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-4"
      />
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("temperature_units")}
        id="temperature_units"
        name="temperature_units"
        value={values.temperature_units}
        options={TEMPRATURE_UNITS_OPTIONS}
        onChange={(e) => {
          formikSetValue("temperature_units", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("temperature_units", true);
          handleBlur(e);
        }}
        touched={touched.temperature_units}
        error={errors.temperature_units}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("latitude_longitude_format")}
        id="latitude_longitude_format"
        name="latitude_longitude_format"
        value={values.latitude_longitude_format}
        options={LATITUDE_LONGITUDE_FORMAT_OPTIONS}
        onChange={(e) => {
          formikSetValue("latitude_longitude_format", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("latitude_longitude_format", true);
          handleBlur(e);
        }}
        touched={touched.latitude_longitude_format}
        error={errors.latitude_longitude_format}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {/* <div className="col-span-12 grid grid-cols-6"> // field removed per client request
        <AdminFormFieldInput
          label={t("route_segment_color_rule")}
          type="text"
          id="route_segment_color_rule"
          name="route_segment_color_rule"
          value={values.route_segment_color_rule}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.route_segment_color_rule}
          touched={touched.route_segment_color_rule}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div> */}

      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("route_line_thickness")}
        id="route_line_thickness"
        name="route_line_thickness"
        value={values.route_line_thickness}
        options={ROUTE_LINE_THICKNESS_OPTIONS}
        onChange={(e) => {
          formikSetValue("route_line_thickness", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("route_line_thickness", true);
          handleBlur(e);
        }}
        touched={touched.route_line_thickness}
        error={errors.route_line_thickness}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("multi_vehicle_map_name")}
        type="text"
        id="multi_vehicle_map_name"
        name="multi_vehicle_map_name"
        value={values.multi_vehicle_map_name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.multi_vehicle_map_name}
        touched={touched.multi_vehicle_map_name}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {/* <AdminFormFieldInput
        label={t("device_title")}
        type="text"
        id="device_title"
        name="device_title"
        value={values.device_title}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.device_title}
        touched={touched.device_title}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("device_title_plural")}
        type="text"
        id="device_title_plural"
        name="device_title_plural"
        value={values.device_title_plural}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.device_title_plural}
        touched={touched.device_title_plural}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("device_group_title")}
        type="text"
        id="device_group_title"
        name="device_group_title"
        value={values.device_group_title}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.device_group_title}
        touched={touched.device_group_title}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("device_group_title_plural")}
        type="text"
        id="device_group_title_plural"
        name="device_group_title_plural"
        value={values.device_group_title_plural}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.device_group_title_plural}
        touched={touched.device_group_title_plural}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("address_title")}
        type="text"
        id="address_title"
        name="address_title"
        value={values.address_title}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.address_title}
        touched={touched.address_title}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label={t("address_title_plural")}
        type="text"
        id="address_title_plural"
        name="address_title_plural"
        value={values.address_title_plural}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.address_title_plural}
        touched={touched.address_title_plural}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}

      {/* <AdminFormFieldInput
        label={t("default_login_userId")}
        type="text"
        id="default_login_userId"
        name="default_login_userId"
        value={values.default_login_userId}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.default_login_userId}
        touched={touched.default_login_userId}
        disabled={!userCanEdit}
        detailsFormField={true}
      /> */}
      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("default_overlay")}
        id="default_overlay"
        name="default_overlay"
        value={values.default_overlay}
        options={DEFAULT_OVERLAY_OPTIONS}
        onChange={(e) => {
          formikSetValue("default_overlay", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("default_overlay", true);
          handleBlur(e);
        }}
        touched={touched.default_overlay}
        error={errors.default_overlay}
        disabled={!userCanEdit}
        detailsFormField={true}
      />
      <AdminFormFieldInput
        label={t("minidleseconds")}
        type="number"
        id="minidleseconds"
        name="minidleseconds"
        value={values.minidleseconds}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.minidleseconds}
        error={errors.minidleseconds}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />
      {/* <AdminFormFieldInput
        label={t("cameraliveviewduration")}
        type="number"
        id="cameraliveviewduration"
        name="cameraliveviewduration"
        value={values.cameraliveviewduration}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.cameraliveviewduration}
        error={errors.cameraliveviewduration}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      /> */}
      <AdminFormFieldDurationInput
        label={t("cameraliveviewduration")}
        id="cameraliveviewduration"
        name="cameraliveviewduration"
        value={values.cameraliveviewduration}
        loading={loadingData}
        onChange={(totalSeconds) => {
          formikSetValue("cameraliveviewduration", totalSeconds);
        }}
        onBlur={() => {
          formikSetTouched("cameraliveviewduration", true);
        }}
        touched={touched.cameraliveviewduration}
        error={errors.cameraliveviewduration}
        disabled={!userCanEdit}
        detailsFormField={true}
        minutesAsInput={true}
        showSeconds={true}
        hideHours={true}
        maxMinutes={10}
      />


      <AdminFormFieldDropdown
        loadingData={loadingData}
        label={t("map_icon_size")}
        id="map_icon_size"
        name="map_icon_size"
        value={
          !!values.map_icon_size
            ? values.map_icon_size.toString()
            : ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE.toString()
        }
        options={ACCOUNT_MAP_ICON_SIZE_OPTIONS}
        onChange={(e) => {
          formikSetValue("map_icon_size", e?.value);
        }}
        onBlur={(e) => {
          formikSetTouched("map_icon_size", true);
          handleBlur(e);
        }}
        touched={touched.map_icon_size}
        error={errors.map_icon_size}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          label={t("infobox_always_open")}
          id="infobox_always_open"
          type="checkbox"
          name="infobox_always_open"
          checked={values.infobox_always_open}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.infobox_always_open}
          error={errors.infobox_always_open}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <div className="col-span-12 grid grid-cols-8">
        <AdminFormFieldCheckbox
          label={t("hide_asset_map_pinpoints")}
          id="hide_asset_map_pinpoints"
          type="checkbox"
          name="hide_asset_map_pinpoints"
          checked={values.hide_asset_map_pinpoints}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.hide_asset_map_pinpoints}
          error={errors.hide_asset_map_pinpoints}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>

      <AdminFormFieldMultiCheckbox
        label={t("map_infobox_information")}
        id="infobox_display_fields"
        options={INFOBOX_DISPLAY_OPTIONS}
        values={{
          infobox_asset_id: values.infobox_asset_id,
          infobox_asset_desc: values.infobox_asset_desc,
          infobox_asset_display: values.infobox_asset_display,
          infobox_driver: values.infobox_driver,
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-12"
      />
    </div>
  );
};

export const AccountMaintenanceIntervalLabelForm: FC<
  AccountGeneralDetailFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  maintenanceFlag,
  setModifiedCheckBoxData,
}) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "dashboard.profile.account.form",
    });
    // State to manage checkbox states
    const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
      {}
    );

    // Handle checkbox changes
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, checked } = e.target;
      setCheckboxState((prevState) => ({
        ...prevState,
        [id]: checked,
      }));
      setModifiedCheckBoxData?.((prevState) => ({
        ...prevState,
        [id]: checked,
      }));
    };

    useEffect(() => {
      if (maintenanceFlag) {
        const checkboxData = {
          last_maintenance_1: !!maintenanceFlag?.maint_flag_km0,
          last_maintenance_2: !!maintenanceFlag?.maint_flag_km1,
          last_maintenance_3: !!maintenanceFlag?.maint_flag_km2,
          last_maintenance_4: !!maintenanceFlag?.maint_flag_km3,
          last_maintenance_5: !!maintenanceFlag?.maint_flag_km4,
          last_maintenance_6: !!maintenanceFlag?.maint_flag_km5,
          last_maintenance_7: !!maintenanceFlag?.maint_flag_km6,
          last_maintenance_8: !!maintenanceFlag?.maint_flag_km7,
          last_maintenance_9: !!maintenanceFlag?.maint_flag_km8,
          last_maintenance_10: !!maintenanceFlag?.maint_flag_km9,
          last_eng_hours_maint_1: !!maintenanceFlag?.maint_flag_hr0,
          last_eng_hours_maint_2: !!maintenanceFlag?.maint_flag_hr1,
          last_eng_hours_maint_3: !!maintenanceFlag?.maint_flag_hr2,
          last_eng_hours_maint_4: !!maintenanceFlag?.maint_flag_hr3,
          last_eng_hours_maint_5: !!maintenanceFlag?.maint_flag_hr4,
          last_service_time_1: !!maintenanceFlag?.maint_flag_ft0,
          last_service_time_2: !!maintenanceFlag?.maint_flag_ft1,
          last_service_time_3: !!maintenanceFlag?.maint_flag_ft2,
          last_service_time_4: !!maintenanceFlag?.maint_flag_ft3,
          last_service_time_5: !!maintenanceFlag?.maint_flag_ft4,
        };
        setCheckboxState(checkboxData);
      }
    }, [maintenanceFlag]);
    return (
      <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-2 gap-4">
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_1"
            wrapperClass="w-6 h-6"
            isChecked={!!checkboxState["last_maintenance_1"]}
            handleChange={handleCheckboxChange}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_1")}
            type="text"
            id="last_maintenance_1"
            name="last_maintenance_1"
            value={values.last_maintenance_1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_1}
            touched={touched.last_maintenance_1}
            disabled={!(userCanEdit && checkboxState["last_maintenance_1"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_2"
            wrapperClass="w-6 h-6"
            isChecked={!!checkboxState["last_maintenance_2"]}
            handleChange={handleCheckboxChange}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_2")}
            type="text"
            id="last_maintenance_2"
            name="last_maintenance_2"
            value={values.last_maintenance_2}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_2}
            touched={touched.last_maintenance_2}
            disabled={!(userCanEdit && checkboxState["last_maintenance_2"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_3"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_3"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_3")}
            type="text"
            id="last_maintenance_3"
            name="last_maintenance_3"
            value={values.last_maintenance_3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_3}
            touched={touched.last_maintenance_3}
            disabled={!(userCanEdit && checkboxState["last_maintenance_3"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_4"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_4"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_4")}
            type="text"
            id="last_maintenance_4"
            name="last_maintenance_4"
            value={values.last_maintenance_4}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_4}
            touched={touched.last_maintenance_4}
            disabled={!(userCanEdit && checkboxState["last_maintenance_4"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_5"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_5"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_5")}
            type="text"
            id="last_maintenance_5"
            name="last_maintenance_5"
            value={values.last_maintenance_5}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_5}
            touched={touched.last_maintenance_5}
            disabled={!(userCanEdit && checkboxState["last_maintenance_5"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_6"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_6"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_6")}
            type="text"
            id="last_maintenance_6"
            name="last_maintenance_6"
            value={values.last_maintenance_6}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_6}
            touched={touched.last_maintenance_6}
            disabled={!(userCanEdit && checkboxState["last_maintenance_6"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_7"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_7"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_7")}
            type="text"
            id="last_maintenance_7"
            name="last_maintenance_7"
            value={values.last_maintenance_7}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_7}
            touched={touched.last_maintenance_7}
            disabled={!(userCanEdit && checkboxState["last_maintenance_7"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_8"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_8"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_8")}
            type="text"
            id="last_maintenance_8"
            name="last_maintenance_8"
            value={values.last_maintenance_8}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_8}
            touched={touched.last_maintenance_8}
            disabled={!(userCanEdit && checkboxState["last_maintenance_8"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_9"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_9"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_9")}
            type="text"
            id="last_maintenance_9"
            name="last_maintenance_9"
            value={values.last_maintenance_9}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_9}
            touched={touched.last_maintenance_9}
            disabled={!(userCanEdit && checkboxState["last_maintenance_9"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_maintenance_10"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_maintenance_10"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_maintenance_10")}
            type="text"
            id="last_maintenance_10"
            name="last_maintenance_10"
            value={values.last_maintenance_10}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_maintenance_10}
            touched={touched.last_maintenance_10}
            disabled={!(userCanEdit && checkboxState["last_maintenance_10"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="col-span-2 pt-6"></div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_1"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_1"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_1")}
            type="text"
            id="last_eng_hours_maint_1"
            name="last_eng_hours_maint_1"
            value={values.last_eng_hours_maint_1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_1}
            touched={touched.last_eng_hours_maint_1}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_1"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_2"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_2"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_2")}
            type="text"
            id="last_eng_hours_maint_2"
            name="last_eng_hours_maint_2"
            value={values.last_eng_hours_maint_2}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_2}
            touched={touched.last_eng_hours_maint_2}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_2"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_3"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_3"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_3")}
            type="text"
            id="last_eng_hours_maint_3"
            name="last_eng_hours_maint_3"
            value={values.last_eng_hours_maint_3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_3}
            touched={touched.last_eng_hours_maint_3}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_3"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_eng_hours_maint_4"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_eng_hours_maint_4"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_eng_hours_maint_4")}
            type="text"
            id="last_eng_hours_maint_4"
            name="last_eng_hours_maint_4"
            value={values.last_eng_hours_maint_4}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_eng_hours_maint_4}
            touched={touched.last_eng_hours_maint_4}
            disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_4"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="col-span-2 grid grid-cols-2 gap-4">
          <div className="flex w-full gap-1">
            <TickCheckbox
              id="last_eng_hours_maint_5"
              wrapperClass="w-6 h-6"
              handleChange={handleCheckboxChange}
              isChecked={!!checkboxState["last_eng_hours_maint_5"]}
              disabled={!userCanEdit}
            />
            <AdminFormFieldInput
              label={t("last_eng_hours_maint_5")}
              type="text"
              id="last_eng_hours_maint_5"
              name="last_eng_hours_maint_5"
              value={values.last_eng_hours_maint_5}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.last_eng_hours_maint_5}
              touched={touched.last_eng_hours_maint_5}
              disabled={!(userCanEdit && checkboxState["last_eng_hours_maint_5"])}
              detailsFormField={true}
              customWrapperClass="!w-full"
            />
          </div>
        </div>
        <div className="col-span-2 pt-6"></div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_1"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_1"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_1")}
            type="text"
            id="last_service_time_1"
            name="last_service_time_1"
            value={values.last_service_time_1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_1}
            touched={touched.last_service_time_1}
            disabled={!(userCanEdit && checkboxState["last_service_time_1"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_2"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_2"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_2")}
            type="text"
            id="last_service_time_2"
            name="last_service_time_2"
            value={values.last_service_time_2}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_2}
            touched={touched.last_service_time_2}
            disabled={!(userCanEdit && checkboxState["last_service_time_2"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_3"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_3"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_3")}
            type="text"
            id="last_service_time_3"
            name="last_service_time_3"
            value={values.last_service_time_3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_3}
            touched={touched.last_service_time_3}
            disabled={!(userCanEdit && checkboxState["last_service_time_3"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_4"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_4"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_4")}
            type="text"
            id="last_service_time_4"
            name="last_service_time_4"
            value={values.last_service_time_4}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_4}
            touched={touched.last_service_time_4}
            disabled={!(userCanEdit && checkboxState["last_service_time_4"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
        <div className="flex w-full gap-1">
          <TickCheckbox
            id="last_service_time_5"
            wrapperClass="w-6 h-6"
            handleChange={handleCheckboxChange}
            isChecked={!!checkboxState["last_service_time_5"]}
            disabled={!userCanEdit}
          />
          <AdminFormFieldInput
            label={t("last_service_time_5")}
            type="text"
            id="last_service_time_5"
            name="last_service_time_5"
            value={values.last_service_time_5}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.last_service_time_5}
            touched={touched.last_service_time_5}
            disabled={!(userCanEdit && checkboxState["last_service_time_5"])}
            detailsFormField={true}
            customWrapperClass="!w-full"
          />
        </div>
      </div>
    );
  };

export const AccountScorecardWeightFactorsForm: FC<
  AccountGeneralDetailFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {[
        "harsh_braking",
        "harsh_acceleration",
        "speeding",
        "reverse",
        "seatbelt_off",
        "harsh_cornering",
        "idle_ratio",
        "impact_crash_ai",
        "cellphone_use_ai",
        "distracted_driving_ai",
        "drinking_eating_ai",
        "smoking_ai",
        "possible_fatiuge_ai",
        "obstructed_camera_ai",
        "tailgating_ai",
        "scorewtfrontcollisionai",
        "scorewtpedestriancollisionai",
        "scorewtlanedepartureai",
        "scorewtstopandgoai",
        "scorewtrollingstopai",
        "scorewtcamerapowerlossai",
        "scorewtcameracoverremovalai",
        "scorewt75for30ai",
      ].map((field, i) => (
        <AdminFormFieldDropdown
          key={`field_${i}`}
          loadingData={loadingData}
          label={t(field)}
          id={field}
          name={field}
          value={values?.[field]}
          options={SCORECARD_WEIGHT_FACTOR_OPTIONS}
          onChange={(e) => {
            formikSetValue(field, e?.value);
          }}
          onBlur={(e) => {
            formikSetTouched(field, true);
            handleBlur(e);
          }}
          touched={touched?.[field]}
          error={errors?.[field]}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      ))}
    </div>
  );
};

export const AccountAssetLabelConfigForm: FC<AccountGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  formikValidateForm,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "dashboard.profile.account.form",
  });
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const assetLabelItems = useMemo<TSortableToggleItem[]>(() => {
    const allowedLabelComponents =
      APP_CONFIG.ASSET_CONFIGS.LABEL_COMPONENTS.filter(
        (option) => showDriver || option.value !== "drivername"
      );
    const allowedValues = allowedLabelComponents.map((option) => option.value);

    // Default only when the field is unset; preserve [] so users can uncheck all.
    const rawSelectedValues = Array.isArray(values.asset_label_fields)
      ? values.asset_label_fields
      : APP_CONFIG.ASSET_CONFIGS.DEFAULT_LABEL_CONFIG;

    const selectedValues = rawSelectedValues.filter((value: string) =>
      allowedValues.includes(value)
    );

    const labelByValue = new Map(
      allowedLabelComponents.map((option) => [option.value, option.label])
    );

    const orderedEnabled = selectedValues.map((value: string) => ({
      value,
      label: labelByValue.get(value) ?? value,
      enabled: true,
    }));

    const disabledItems = allowedLabelComponents
      .filter((option) => !selectedValues.includes(option.value))
      .map((option) => ({
        ...option,
        enabled: false,
      }));

    return [...orderedEnabled, ...disabledItems];
  }, [showDriver, values.asset_label_fields]);

  const assetSearchableItems = useMemo<TSortableToggleItem[]>(() => {
    const allowedSearchableFields =
      APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS.filter(
        (option) =>
          showDriver || !["drivername", "driverid"].includes(option.value)
      );
    const allowedValues = allowedSearchableFields.map((option) => option.value);

    const selectedValues = Array.isArray(values.asset_searchable_fields)
      ? values.asset_searchable_fields.filter((value: string) =>
        allowedValues.includes(value)
      )
      : // all values in APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS are searchable by default if not set in DB
      allowedValues;

    // Keep a stable display order for non-draggable mode.
    return allowedSearchableFields.map((option) => ({
      ...option,
      enabled: selectedValues.includes(option.value),
    }));
  }, [showDriver, values.asset_searchable_fields]);

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldDraggableToggleList
        loadingData={loadingData}
        label={t("asset_label_fields")}
        id="asset_label_fields"
        items={assetLabelItems}
        isDraggable={true}
        previewRenderer={(currentItems) => {
          const previewText = currentItems
            .filter((item) => item.enabled)
            .map((item) => item.label)
            .join(" | ");

          return (
            <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700 break-words">
              <span className="font-semibold">Preview:</span>{" "}
              {previewText || "-"}
            </div>
          );
        }}
        onChange={async (updatedItems) => {
          const nextLabelValues = updatedItems
            .filter((item) => item.enabled)
            .map((item) => item.value);

          const allowedSearchableValues =
            APP_CONFIG.ASSET_CONFIGS.SEARCHABLE_FIELDS.filter(
              (option) =>
                showDriver || !["drivername", "driverid"].includes(option.value)
            ).map((option) => option.value);

          const currentSearchableValues = Array.isArray(
            values.asset_searchable_fields
          )
            ? values.asset_searchable_fields.filter((value: string) =>
              allowedSearchableValues.includes(value)
            )
            : allowedSearchableValues;

          const nextSearchableValues = syncMirroredAssetToggles(
            nextLabelValues,
            currentSearchableValues,
            allowedSearchableValues
          );

          await Promise.all([
            formikSetValue("asset_label_fields", nextLabelValues, false),
            formikSetValue(
              "asset_searchable_fields",
              nextSearchableValues,
              false
            ),
          ]);

          await formikValidateForm?.();
        }}
        onBlur={() => {
          formikSetTouched("asset_label_fields", true, false);
        }}
        touched={touched.asset_label_fields}
        error={errors.asset_label_fields}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />

      <AdminFormFieldDraggableToggleList
        loadingData={loadingData}
        label={t("asset_searchable_fields")}
        id="asset_searchable_fields"
        items={assetSearchableItems}
        isDraggable={false}
        onChange={async (updatedItems) => {
          const nextSearchableValues = updatedItems
            .filter((item) => item.enabled)
            .map((item) => item.value);

          const allowedLabelValues =
            APP_CONFIG.ASSET_CONFIGS.LABEL_COMPONENTS.filter(
              (option) => showDriver || option.value !== "drivername"
            ).map((option) => option.value);

          const currentLabelValues = (
            Array.isArray(values.asset_label_fields)
              ? values.asset_label_fields
              : APP_CONFIG.ASSET_CONFIGS.DEFAULT_LABEL_CONFIG
          ).filter((value: string) => allowedLabelValues.includes(value));

          const nextLabelValues = syncMirroredAssetToggles(
            nextSearchableValues,
            currentLabelValues,
            allowedLabelValues
          );

          await Promise.all([
            formikSetValue(
              "asset_searchable_fields",
              nextSearchableValues,
              false
            ),
            formikSetValue("asset_label_fields", nextLabelValues, false),
          ]);

          await formikValidateForm?.();
        }}
        onBlur={() => {
          formikSetTouched("asset_searchable_fields", true, false);
        }}
        touched={touched.asset_searchable_fields}
        error={errors.asset_searchable_fields}
        disabled={!userCanEdit}
        detailsFormField={true}
        customWrapperClass="col-span-6"
      />

      {APP_CONFIG.DEBUG.ALL && (
        <>
          <div className="col-span-6">
            <pre>{JSON.stringify(values.asset_label_fields, null, 2)}</pre>
          </div>
          <div className="col-span-6">
            <pre>{JSON.stringify(values.asset_searchable_fields, null, 2)}</pre>
          </div>
        </>
      )}
    </div>
  );
};
